import {
  CreditCard,
  MapPinned,
  Sparkles,
  ArrowRight,
  Wallet,
  Plane,
  BadgeDollarSign,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const HowItWorksSection = () => {
  const navigate = useNavigate()
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#f8fafc] via-white to-[#eef4ff] py-24 px-6 lg:px-16">

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-300/20 blur-3xl rounded-full animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-300/20 blur-3xl rounded-full animate-blob"></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
        <svg
          className="absolute inset-0 w-full h-full opacity-20"
          viewBox="0 0 1400 800"
          fill="none"
        >
          <path
            d="M120 520 C320 180, 900 120, 1220 340"
            stroke="#D4AF37"
            strokeWidth="2"
            strokeDasharray="10 10"
            className="animate-route"
          />
          <g className="animate-plane">
            <text x="0" y="0" fontSize="28">
              ✈️
            </text>
          </g>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-[11px] font-black uppercase tracking-[0.2em] text-[#D4AF37] mb-8">
            <Sparkles size={14} />
            How It Works
          </div>

          <h2 className="text-5xl lg:text-6xl font-black text-slate-900 leading-tight tracking-tight">
            Turn Your Reward Points Into
            <span className="block text-[#D4AF37]">
              Smarter Travel Opportunities
            </span>
          </h2>

          <p className="text-slate-600 text-lg leading-relaxed mt-8 max-w-3xl mx-auto">
            Add your reward points, select your preferred travel card,
            choose your destination, and discover estimated redemption
            opportunities tailored to your travel preferences.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8 mt-20">
          <div className="relative bg-white border border-slate-200 shadow-[0_10px_50px_rgba(15,23,42,0.08)] rounded-[2rem] p-8 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300">
            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
              <Wallet size={30} className="text-[#D4AF37]" />
            </div>

            <div className="absolute top-6 right-6 text-5xl font-black text-slate-100">
              01
            </div>

            <h3 className="text-2xl font-black text-slate-900">
              Add Your Points
            </h3>

            <p className="text-slate-600 text-sm leading-relaxed mt-4">
              Enter your available reward points or airline miles to begin
              exploring estimated travel redemption opportunities.
            </p>
            <div className="mt-6 p-4 rounded-2xl bg-slate-50 border border-slate-100">

              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                Example
              </p>

              <h4 className="text-3xl font-black text-[#D4AF37] mt-2">
                82,000
              </h4>

              <p className="text-xs text-slate-500 mt-1">
                Available Reward Points
              </p>
            </div>
          </div>

          <div className="relative bg-white border border-slate-200 shadow-[0_10px_50px_rgba(15,23,42,0.08)] rounded-[2rem] p-8 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300">
            <div className="w-16 h-16 rounded-2xl bg-cyan-100 flex items-center justify-center mb-6">
              <CreditCard size={30} className="text-cyan-600" />
            </div>

            <div className="absolute top-6 right-6 text-5xl font-black text-slate-100">
              02
            </div>

            <h3 className="text-2xl font-black text-slate-900">
              Select Your Card
            </h3>

            <p className="text-slate-600 text-sm leading-relaxed mt-4">
              Choose your credit card or loyalty program to compare estimated
              transfer partner opportunities and reward value.
            </p>

            {/* Example */}
            <div className="mt-6 p-4 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-between">

              <div>
                <p className="text-xs font-black text-slate-900">
                  Chase Sapphire
                </p>

                <p className="text-[11px] text-slate-500 mt-1">
                  Travel Rewards Program
                </p>
              </div>

              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                <CreditCard size={18} className="text-[#D4AF37]" />
              </div>
            </div>
          </div>

          <div className="relative bg-white border border-slate-200 shadow-[0_10px_50px_rgba(15,23,42,0.08)] rounded-[2rem] p-8 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-6">
              <MapPinned size={30} className="text-emerald-600" />
            </div>

            <div className="absolute top-6 right-6 text-5xl font-black text-slate-100">
              03
            </div>

            <h3 className="text-2xl font-black text-slate-900">
              Choose Your Trip
            </h3>

            <p className="text-slate-600 text-sm leading-relaxed mt-4">
              Select your departure city, destination, cabin type, and travel
              preferences to personalize your estimated reward suggestions.
            </p>

            {/* Example */}
            <div className="mt-6 p-4 rounded-2xl bg-slate-50 border border-slate-100">

              <div className="flex items-center justify-between">

                <div>
                  <p className="text-xs font-black text-slate-900">
                    NYC → London
                  </p>

                  <p className="text-[11px] text-slate-500 mt-1">
                    Business Class
                  </p>
                </div>

                <Plane size={18} className="text-[#D4AF37]" />
              </div>
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-[#D4AF37] to-yellow-400 rounded-[2rem] p-8 text-black hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/20 blur-3xl"></div>

            <div className="w-16 h-16 rounded-2xl bg-black/10 flex items-center justify-center mb-6 relative z-10">
              <BadgeDollarSign size={30} />
            </div>

            <div className="absolute top-6 right-6 text-5xl font-black text-black/10">
              04
            </div>

            <div className="relative z-10">

              <h3 className="text-2xl font-black">
                Get Smart Suggestions
              </h3>

              <p className="text-black/70 text-sm leading-relaxed mt-4">
                Receive estimated redemption opportunities, premium cabin
                insights, and smarter ways to maximize your reward value.
              </p>

              {/* Example */}
              <div className="mt-6 p-4 rounded-2xl bg-black/10 border border-black/10">

                <p className="text-[10px] uppercase tracking-[0.2em] font-black">
                  Estimated Travel Value
                </p>

                <h4 className="text-4xl font-black mt-2">
                  Up to $1,284
                </h4>

                <p className="text-xs mt-1 text-black/70">
                  Potential premium redemption opportunity
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 text-center">
          <button 
          onClick={() => navigate("/")}
          className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-yellow-400 transition-all text-black font-black px-10 py-5 rounded-2xl shadow-2xl shadow-yellow-500/20">
            Start Exploring Reward Opportunities
            <ArrowRight
              size={18}
              className="group-hover:translate-x-1 transition-all"
            />
          </button>

          <p className="text-slate-500 text-sm mt-6 max-w-2xl mx-auto">
            Redemption estimates are based on publicly available travel pricing,
            airline transfer partners, and historical reward trends. Actual
            values may vary depending on availability and booking dates.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;